import { useMemo } from 'react';

import parse, { DOMNode, Element } from 'html-react-parser';
import { marked } from 'marked';

import { ArtifactBlock } from './artifact-block';
import { EndBlock } from './end-block';
import { KimChronologySection } from './kim-chronology-section';
import { Reference } from './reference';
import { UserMessage } from './user-message';
import {
  ArtifactBlock as ArtifactBlockType,
  ArtifactType,
  ContentBlock,
  KimMessageType,
  ReferenceType,
} from '../../../../../../../../../../../backend/src/types/types';
import { KIM_MARKED_OPTIONS } from '../../utils/kim-markdown';
import { KimThreadFile } from '@/api/types/backend-dto-types';
import { cn } from '@/new/lib/utils';

export const REF_PLACEHOLDER_TAG = 'kimref';
const ARTIFACT_PLACEHOLDER_TAG = 'kimartifact';

const CLICKABLE_REF_TYPES = new Set<ReferenceType>([
  ReferenceType.Fact,
  ReferenceType.Chunk,
  ReferenceType.Document,
  ReferenceType.ThreadFileChunk,
]);

export function formatContent(blocks: ContentBlock[]): string {
  return blocks
    .map((block) => {
      if (block.type === 'text') return block.value.startsWith('#') ? `\n\n${block.value}` : block.value;
      if (block.type === 'artifact')
        return `<${ARTIFACT_PLACEHOLDER_TAG} data-id="${block.id}-${block.versionNumber}"></${ARTIFACT_PLACEHOLDER_TAG}>`;
      if (block.type === 'reference')
        return `<${REF_PLACEHOLDER_TAG} data-id="${block.id}" data-ref-type="${block.refType}"></${REF_PLACEHOLDER_TAG}>`;
      // Survey blocks are rendered separately near the input, not inline.
      return '';
    })
    .join('');
}

export function getReferenceReplacer(
  referenceIndexMap: Map<string, { index: number; referenceType: ReferenceType }>,
  onReferenceClick?: (id: string, referenceType: ReferenceType) => void,
) {
  return (domNode: DOMNode) => {
    if (domNode instanceof Element && domNode.name === REF_PLACEHOLDER_TAG) {
      const id = domNode.attribs['data-id'];
      const refType = domNode.attribs['data-ref-type'] as ReferenceType;
      return (
        <Reference
          index={referenceIndexMap.get(id)?.index ?? 0}
          id={id}
          referenceType={refType}
          onClick={CLICKABLE_REF_TYPES.has(refType) ? onReferenceClick : undefined}
        />
      );
    }
  };
}

export function KimMessage({
  messageId,
  type,
  content,
  isComplete,
  referenceIndexMap,
  onReferenceClick,
  onDownloadClick,
  artifactsById,
  latestArtifactId,
  isDownloading,
  createdAt,
  files,
  isOwnThread,
}: {
  messageId?: string;
  type: KimMessageType;
  content: ContentBlock[];
  isComplete: boolean;
  referenceIndexMap: Map<string, { index: number; referenceType: ReferenceType }>;
  onReferenceClick?: (id: string, referenceType: ReferenceType) => void;
  onDownloadClick?: (artifactId?: string) => void;
  artifactsById?: Map<string, ArtifactBlockType & { published: boolean | undefined }>;
  latestArtifactId?: string | null;
  isDownloading?: boolean;
  createdAt?: string;
  files?: KimThreadFile[];
  isOwnThread?: boolean;
}) {
  const isUser = type === KimMessageType.User;

  const hasChronology = useMemo(
    () => content.some((block) => block.type === 'artifact' && block.artifactType === ArtifactType.Chronology),
    [content],
  );

  const { factIds, chunkIds, directDocIds } = useMemo(() => {
    const factIds = new Set<string>();
    const chunkIds = new Set<string>();
    const directDocIds = new Set<string>();

    const collect = (refType: ReferenceType, id: string) => {
      if (refType === ReferenceType.Fact) factIds.add(id);
      else if (refType === ReferenceType.Chunk) chunkIds.add(id);
      else if (refType === ReferenceType.Document) directDocIds.add(id);
    };

    for (const block of content) {
      if (block.type === 'reference') {
        collect(block.refType, block.id);
      }
      if (block.type === 'artifact') {
        block.content.forEach((artifact) => {
          if (artifact.type === 'reference') {
            collect(artifact.refType, artifact.id);
          }
        });
      }
    }
    return {
      factIds: Array.from(factIds),
      chunkIds: Array.from(chunkIds),
      directDocIds: Array.from(directDocIds),
    };
  }, [content]);

  const hasReferences = useMemo(() => {
    const isCitableRef = (refType: ReferenceType) =>
      refType === ReferenceType.Fact || refType === ReferenceType.Chunk || refType === ReferenceType.Document;
    for (const block of content) {
      if (block.type === 'reference' && isCitableRef(block.refType)) return true;
      if (block.type === 'artifact') {
        for (const artifact of block.content) {
          if (artifact.type === 'reference' && isCitableRef(artifact.refType)) return true;
        }
      }
    }
    return false;
  }, [content]);

  const segments = useMemo(() => {
    type TextSegment = { type: 'text'; blocks: ContentBlock[] };
    type ArtifactSegment = { type: 'artifact'; id: string; artifact: ArtifactBlockType };
    type ChronologySegment = { type: 'chronology'; factIds: string[] };
    type Segment = TextSegment | ArtifactSegment | ChronologySegment;

    const result: Segment[] = [];
    let textBlocks: ContentBlock[] = [];

    const flushText = () => {
      if (textBlocks.length > 0) {
        result.push({ type: 'text', blocks: textBlocks });
        textBlocks = [];
      }
    };

    for (const block of content) {
      if (block.type === 'artifact' && block.artifactType === ArtifactType.Chronology) {
        flushText();
        const factIds = block.content
          .filter(
            (c): c is Extract<typeof c, { type: 'reference' }> =>
              c.type === 'reference' && c.refType === ReferenceType.Fact,
          )
          .map((c) => c.id);
        if (factIds.length > 0) {
          result.push({ type: 'chronology', factIds });
        }
      } else if (block.type === 'artifact') {
        flushText();
        const id = `${block.id}-${block.versionNumber}`;
        const artifact = artifactsById?.get(id);
        if (artifact) result.push({ type: 'artifact', id, artifact });
      } else if (block.type === 'survey') {
        // Survey blocks are rendered separately near the input, not inline.
        continue;
      } else {
        textBlocks.push(block);
      }
    }

    flushText();

    return result.map((segment, i) => {
      if (segment.type === 'chronology') {
        return (
          <KimChronologySection
            key={`chron-${i}`}
            factIds={segment.factIds}
            referenceIndexMap={referenceIndexMap}
            onReferenceClick={onReferenceClick}
          />
        );
      }

      if (segment.type === 'artifact') {
        return (
          <ArtifactBlock
            key={segment.id}
            id={segment.id}
            content={segment.artifact.content}
            referenceIndexMap={referenceIndexMap}
            onReferenceClick={onReferenceClick}
            onDownloadClick={onDownloadClick ? () => onDownloadClick(segment.id) : undefined}
            collapsed={segment.id !== latestArtifactId}
            version={segment.artifact.versionNumber}
            isDownloading={isDownloading}
            artifactsById={artifactsById}
            isOwnThread={isOwnThread}
          />
        );
      }

      const combined = formatContent(segment.blocks);
      const rawHtml = marked.parse(combined, KIM_MARKED_OPTIONS) as string;
      const html = rawHtml.replace(
        new RegExp(`(<\\/${REF_PLACEHOLDER_TAG}>)\\s+(<${REF_PLACEHOLDER_TAG})`, 'g'),
        '$1$2',
      );
      const parsed = parse(html, { replace: getReferenceReplacer(referenceIndexMap, onReferenceClick) });

      return (
        <div
          key={i}
          className={cn(type === KimMessageType.Ai && 'px-4', 'mx-auto flex max-w-3xl items-start gap-3.25')}
        >
          {i === 0 && type === KimMessageType.AiReasoning && (
            <div className="mt-2.25 size-1.25 shrink-0 rounded-full bg-gray-300" />
          )}
          <div
            className={cn(
              'prose prose-sm prose-headings:my-3 prose-a:hover:text-blue-600 prose-a:duration-150 prose-a:text-blue-500 prose-h1:text-2xl prose-h2:text-xl prose-h3:text-lg prose-hr:my-6 prose-p:leading-normal prose-p:text-gray-900 prose-headings:font-semibold max-w-none',
              type === KimMessageType.AiReasoning && 'prose-p:text-gray-600 italic',
            )}
          >
            {parsed}
          </div>
        </div>
      );
    });
  }, [
    content,
    artifactsById,
    referenceIndexMap,
    onReferenceClick,
    onDownloadClick,
    latestArtifactId,
    isDownloading,
    type,
  ]);

  const buildMarkdownToCopy = () =>
    content
      .map((block) => {
        if (block.type === 'text') return block.value;
        if (block.type === 'artifact' && block.artifactType === ArtifactType.Draft) {
          const draftText = block.content.map((c) => (c.type === 'text' ? c.value : '')).join('');
          return `\n\n${draftText}\n\n`;
        }
        return '';
      })
      .join('')
      .replace(/^[ \t]*(?:-{3,}|\*{3,}|_{3,})[ \t]*$/gm, '');

  const getTextToCopy = () => buildMarkdownToCopy();
  const getHtmlToCopy = () => marked.parse(buildMarkdownToCopy(), KIM_MARKED_OPTIONS) as string;

  if (isUser) {
    return (
      <UserMessage
        content={content}
        createdAt={createdAt}
        files={files}
        onFileClick={
          onReferenceClick ? (fileId: string) => onReferenceClick(fileId, ReferenceType.ThreadFile) : undefined
        }
      />
    );
  }

  if (segments.length === 0) {
    return null;
  }

  return (
    <div className="flex">
      <div
        className={cn(
          // the [font-feature-settings:\'calt\'_0] disables converting (1) into a weird unicode char
          "w-full rounded-xl py-2 [font-feature-settings:'calt'_0]",
        )}
      >
        <div className="font-serif">{segments}</div>

        {type === KimMessageType.Ai && isComplete && (
          <EndBlock
            messageId={messageId}
            getTextToCopy={getTextToCopy}
            getHtmlToCopy={getHtmlToCopy}
            factIds={factIds}
            chunkIds={chunkIds}
            directDocIds={directDocIds}
            referenceIndexMap={referenceIndexMap}
            onReferenceClick={onReferenceClick}
            onDownloadClick={onDownloadClick ? () => onDownloadClick() : undefined}
            isDownloading={isDownloading}
            isChronology={hasChronology}
          />
        )}
      </div>
    </div>
  );
}
